import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as teamService from '../services/teamService';
import { RadarChart } from '../components/charts/RadarChart';
import { ChartData } from 'chart.js';

export const TeamComparison: React.FC = () => {
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  
  const { data: teams, isLoading: isLoadingTeams } = useQuery(
    ['teams'],
    () => teamService.getTeams()
  );
  
  const { data: teamAnalyses, isLoading: isLoadingAnalyses } = useQuery(
    ['teamAnalyses', selectedTeamIds],
    async () => {
      const analyses = await Promise.all(
        selectedTeamIds.map(async (id) => {
          const analysis = await teamService.analyzeTeam(id);
          const team = teams?.find(t => t.id === id);
          return {
            id,
            name: team?.name || id,
            analysis
          };
        })
      );
      return analyses;
    },
    {
      enabled: selectedTeamIds.length > 0 && !!teams
    }
  );
  
  const handleTeamToggle = (teamId: string) => {
    setSelectedTeamIds(prev => 
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };
  
  // Prepare chart data
  const chartData: ChartData<'radar'> | null = teamAnalyses ? {
    labels: teamAnalyses.length > 0 
      ? Object.keys(teamAnalyses[0].analysis.capabilities).map(
          cap => cap.charAt(0).toUpperCase() + cap.slice(1)
        )
      : [],
    datasets: teamAnalyses.map((team, index) => {
      // Generate a color based on index
      const hue = (index * 137) % 360;
      
      return {
        label: team.name,
        data: Object.values(team.analysis.capabilities),
        backgroundColor: `hsla(${hue}, 70%, 60%, 0.2)`,
        borderColor: `hsl(${hue}, 70%, 50%)`,
        pointBackgroundColor: `hsl(${hue}, 70%, 50%)`,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: `hsl(${hue}, 70%, 50%)`
      };
    })
  } : null;
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Team Comparison</h1>
        <Link to="/teams" className="text-blue-600 hover:text-blue-800">
          Back to Teams
        </Link>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Select Teams to Compare</h2>
            
            {isLoadingTeams ? (
              <div className="text-center py-4">Loading teams...</div>
            ) : teams && teams.length > 0 ? (
              <div className="space-y-2">
                {teams.map(team => (
                  <div key={team.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`team-${team.id}`}
                      checked={selectedTeamIds.includes(team.id)}
                      onChange={() => handleTeamToggle(team.id)}
                      className="mr-2"
                    />
                    <label htmlFor={`team-${team.id}`} className="text-gray-700">
                      {team.name}
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500">No teams available</div>
            )}
            
            {selectedTeamIds.length > 0 && (
              <div className="mt-4">
                <button
                  className="text-sm text-red-600 hover:text-red-800"
                  onClick={() => setSelectedTeamIds([])}
                >
                  Clear Selection
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="lg:col-span-2">
          {isLoadingAnalyses ? (
            <div className="text-center py-10">Loading team analyses...</div>
          ) : chartData && chartData.datasets.length > 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium mb-4">Team Capability Comparison</h3>
              <div className="flex justify-center">
                <RadarChart 
                  data={chartData} 
                  height={500}
                  width={500}
                  options={{
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: 12
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            return `${context.dataset.label}: ${context.raw}/10`;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="text-gray-500 mb-4">Select teams to compare their capabilities</p>
              <p className="text-sm text-gray-400">
                The radar chart will show the strengths and weaknesses of each team
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
